import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, getMe, login as apiLogin, logout as apiLogout, saveMyPreferences, type Me, type UserPreferences, type Capability } from '../api'
import { DEFAULT_CURRENCY } from '../../shared/currency'
import { setAppCurrency } from '../lookups'

// Holds the logged-in user. The app renders <Login> until `user` is set. Also the
// single source of truth for per-user UI preferences (e.g. table column widths).

interface AuthContextValue {
    user: Me | null
    loading: boolean
    login: (email: string, password: string) => Promise<void>
    logout: () => Promise<void>
    // Re-fetch the current user (e.g. after editing the own profile) so the topbar
    // name and permissions reflect the change.
    refreshUser: () => Promise<void>
    preferences: UserPreferences
    savePreferences: (patch: Partial<UserPreferences>) => Promise<void>
    // RBAC helper: does the current user's role have `cap` on `module`?
    can: (module: string, cap: Capability) => boolean
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    login: async () => {},
    logout: async () => {},
    refreshUser: async () => {},
    preferences: {},
    savePreferences: async () => {},
    can: () => false,
})

export function useAuth() {
    return useContext(AuthContext)
}

// The backoffice currency, from the session.
export function useCurrency(): string {
    return useContext(AuthContext).user?.currency || DEFAULT_CURRENCY
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<Me | null>(null)
    const [loading, setLoading] = useState(true)
    // formatMoney falls back to it — set before the children render with the new user.
    setAppCurrency(user?.currency)

    // Resolve the session on first load (401 → not logged in).
    useEffect(() => {
        getMe().then(setUser).catch(() => setUser(null)).finally(() => setLoading(false))
    }, [])

    // A 401 on any later call means the session expired → drop back to the login
    // screen. The login request itself is exempt (its 401 is a wrong-password case).
    useEffect(() => {
        const id = api.interceptors.response.use(
            (res) => res,
            (err) => {
                const url = err?.config?.url ?? ''
                if (err?.response?.status === 401 && !url.includes('/auth/login')) setUser(null)
                return Promise.reject(err)
            },
        )
        return () => api.interceptors.response.eject(id)
    }, [])

    const login = useCallback(async (email: string, password: string) => {
        setUser(await apiLogin(email, password))
    }, [])

    const logout = useCallback(async () => {
        await apiLogout().catch(() => {})
        setUser(null)
    }, [])

    const refreshUser = useCallback(async () => {
        await getMe().then(setUser).catch(() => {})
    }, [])

    // Optimistic: mirror the server's shallow top-level merge locally right away
    // (so column resets/visibility reflect instantly), then persist.
    const savePreferences = useCallback(async (patch: Partial<UserPreferences>) => {
        setUser((u) => (u ? { ...u, preferences: { ...u.preferences, ...patch } } : u))
        await saveMyPreferences(patch).catch(() => {})
    }, [])

    const can = useCallback(
        (module: string, cap: Capability) => user?.permissions?.[module]?.includes(cap) ?? false,
        [user],
    )

    const value: AuthContextValue = {
        user,
        loading,
        login,
        logout,
        refreshUser,
        preferences: user?.preferences ?? {},
        savePreferences,
        can,
    }
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
